import { useEffect, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { apiClient } from '../../lib/api-client';
import { Trash2, Plus } from 'lucide-react';
import { Skeleton } from '../ui/Skeleton';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ChannelEntry {
  name: string;
  webhookRef: string;
}

export function NotificationChannelsDialog({ open, onClose, onSuccess }: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teamsEnabled, setTeamsEnabled] = useState(true);
  const [channels, setChannels] = useState<ChannelEntry[]>([]);

  useEffect(() => {
    if (!open) return;
    void loadConfig();
  }, [open]);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.getNotificationsMasterConfig();
      const notifications = res?.notifications || {};
      setTeamsEnabled(notifications?.teams?.enabled !== false);
      
      // Convert channels object to array
      const channelsObj = notifications?.teams?.channels || {};
      const channelsArr = Object.entries(channelsObj).map(([name, webhookRef]) => ({
        name,
        webhookRef: webhookRef as string,
      }));
      setChannels(channelsArr.length > 0 ? channelsArr : [{ name: '', webhookRef: '' }]);
    } catch (e: any) {
      setError(e?.message || 'Failed to load notifications config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Convert array back to object, filtering out empty entries
      const channelsObj: Record<string, string> = {};
      for (const ch of channels) {
        if (ch.name.trim() && ch.webhookRef.trim()) {
          channelsObj[ch.name.trim()] = ch.webhookRef.trim();
        }
      }

      const notifications = {
        teams: {
          enabled: teamsEnabled,
          channels: channelsObj,
        },
      };

      await apiClient.updateNotificationsMasterConfig(notifications);
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save notifications config');
    } finally {
      setSaving(false);
    }
  };

  const addChannel = () => {
    setChannels([...channels, { name: '', webhookRef: '' }]);
  };

  const removeChannel = (index: number) => {
    setChannels(channels.filter((_, i) => i !== index));
  };

  const updateChannel = (index: number, field: 'name' | 'webhookRef', value: string) => {
    const updated = [...channels];
    updated[index] = { ...updated[index], [field]: value };
    setChannels(updated);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="3xl">
        <DialogHeader>
          <DialogTitle>Edit Master Notification Channels</DialogTitle>
          <DialogDescription>
            Configure Teams notification channels. Channel names are referenced in tenant configs.
            Webhook refs should match vault secret names (e.g., :teams-webhook-monitoring-alerts).
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {loading && (
            <div className="space-y-4 py-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200 mb-4">
              {error}
            </div>
          )}

          {!loading && (
            <div className="space-y-6">
              {/* Teams Enabled Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="teams-enabled"
                  checked={teamsEnabled}
                  onChange={(e) => setTeamsEnabled(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="teams-enabled" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Teams Notifications Enabled
                </label>
              </div>

              {/* Channels List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Notification Channels
                  </label>
                  <Button variant="outline" size="sm" onClick={addChannel}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Channel
                  </Button>
                </div>

                <div className="space-y-2">
                  {channels.map((channel, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        placeholder="Channel name (e.g., monitoring-alerts)"
                        value={channel.name}
                        onChange={(e) => updateChannel(index, 'name', e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        placeholder="Webhook ref (e.g., :teams-webhook-alerts)"
                        value={channel.webhookRef}
                        onChange={(e) => updateChannel(index, 'webhookRef', e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeChannel(index)}
                        disabled={channels.length <= 1}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Channel names are used in tenant configs to reference these webhooks.
                  Webhook refs starting with ":" will be resolved from vault secrets.
                </p>
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button onClick={onClose} disabled={saving} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
